import { memo, useCallback, useMemo, useState } from 'react';
import DrawerExtraButtons from 'components/DrawerExtraButtons';
import useSelectResource from 'hooks/useSelectResource';
import { getText } from 'i18n/intl';
import {
  changePaddingRule,
  checkPaddingSaveDisabled,
  deletePaddingRule,
  discardChanges,
  firstPaddingRuleSelector,
  paddingRuleMapSelector,
  savePaddingRule,
  secondPaddingRuleSelector,
  setFirstPaddingRule,
  setSecondPaddingRule,
} from 'slices/paddingRules.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import type { DrawerCallback } from 'types/utils.type';
import type { TPaddingRule } from '@timeedit/types';
import { TEDrawer } from '@timeedit/ui-components';
import PaddingRule from './sections/PaddingRule';

interface Props {
  confirmDiscardPaddingChanges: (
    isFirstDrawer: boolean,
    actionFn: () => void
  ) => void;
}

const PaddingRuleDrawer = ({ confirmDiscardPaddingChanges }: Props) => {
  const dispatch = useAppDispatch();

  const firstPaddingRule = useAppSelector(firstPaddingRuleSelector);
  const secondPaddingRule = useAppSelector(secondPaddingRuleSelector);
  const paddingRuleMap: Record<number | string, TPaddingRule> = useAppSelector(
    paddingRuleMapSelector
  );

  const setFirstResource = useCallback(
    (paddingRule: TPaddingRule) => dispatch(setFirstPaddingRule(paddingRule)),
    [dispatch]
  );

  const setSecondResource = useCallback(
    (paddingRule: TPaddingRule) => dispatch(setSecondPaddingRule(paddingRule)),
    [dispatch]
  );

  useSelectResource(
    'integer',
    paddingRuleMap,
    firstPaddingRule,
    secondPaddingRule,
    setFirstResource,
    setSecondResource
  );

  const [isExpand, setIsExpand] = useState(false);

  const firstPaddingRuleSaveDisabled = useMemo(
    () => dispatch(checkPaddingSaveDisabled(true)),
    [firstPaddingRule]
  );

  const secondPaddingRuleSaveDisabled = useMemo(
    () => dispatch(checkPaddingSaveDisabled(false)),
    [secondPaddingRule]
  );

  const onDelete = useCallback((paddingRule: TPaddingRule) => {
    dispatch(deletePaddingRule(paddingRule.id));
  }, []);

  const onSave = useCallback(
    (isFirstDrawer: boolean, paddingRule: TPaddingRule) => {
      dispatch(
        savePaddingRule({ isFirstPaddingRule: isFirstDrawer, paddingRule })
      );
    },
    []
  );

  const onClose = useCallback(
    (isFirstDrawer: boolean) =>
      confirmDiscardPaddingChanges(isFirstDrawer, () => {
        if (isFirstDrawer) {
          dispatch(setFirstPaddingRule(undefined));
        } else {
          dispatch(setSecondPaddingRule(undefined));
        }
      }),
    []
  );

  const onChange = useCallback<DrawerCallback<TPaddingRule>>(
    (isFirstDrawer, key, value) =>
      dispatch(
        changePaddingRule({
          isFirstDrawer,
          key,
          value,
        })
      ),
    []
  );

  return (
    <TEDrawer
      expandWidth="70vw"
      firstDrawer={{
        title: firstPaddingRule?.name || '',
        content: (
          <PaddingRule
            paddingRule={firstPaddingRule}
            onChange={(key, value) => onChange(true, key, value)}
            isExpand={isExpand}
          />
        ),
        drawerOpen: !!firstPaddingRule,
        extra: (
          <DrawerExtraButtons
            confirmDeleteMsg={getText('padding_rule_confirmation_delete')}
            handleDelete={() => onDelete(firstPaddingRule!)}
            handleDiscard={() =>
              dispatch(
                discardChanges({
                  isFirstDrawer: true,
                  id: firstPaddingRule!.id,
                })
              )
            }
            handleSave={() => onSave(true, firstPaddingRule!)}
            isSaveDisable={firstPaddingRuleSaveDisabled as any}
          />
        ),
        setDrawerOpen: (open) => !open && onClose(true),
      }}
      secondDrawer={{
        title: secondPaddingRule?.name || '',
        content: (
          <PaddingRule
            paddingRule={secondPaddingRule}
            onChange={(key, value) => onChange(false, key, value)}
            isExpand={isExpand}
          />
        ),
        drawerOpen: !!secondPaddingRule,
        extra: (
          <DrawerExtraButtons
            confirmDeleteMsg={getText('padding_rule_confirmation_delete')}
            handleDelete={() => onDelete(secondPaddingRule!)}
            handleDiscard={() =>
              dispatch(
                discardChanges({
                  isFirstDrawer: false,
                  id: secondPaddingRule!.id,
                })
              )
            }
            handleSave={() => onSave(false, secondPaddingRule!)}
            isSaveDisable={secondPaddingRuleSaveDisabled as any}
          />
        ),
        setDrawerOpen: (open) => !open && onClose(false),
      }}
      onExpand={setIsExpand}
    />
  );
};

export default memo(PaddingRuleDrawer);
